/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { groupBy, sorted } from "helpers";
import {
  MaterialRead,
  MixRead,
  ProductionMixResultRead,
} from "src/store/api/generatedApi";

export interface PlanTableOptions {
  productGroups: boolean;
  percentages: boolean;
  forecast: boolean;
  steelGrades: string[];
}

export interface ProductionContent {
  groups: PlanGroup[];
  mixes: PlanMixes;
}

export interface PlanMix {
  materials: Record<number, number>;
  total: number;
  maximum: number;
  total_price: number;
  materials_price: number;
  electrical_energy_price: number;
  charged_price: number;
}

export type PlanMixes = Record<number, Record<number, PlanMix>>;

export interface PlanGroup {
  groupId: number;
  // The real mix id.
  mixId: number;
  // The within-chef-group ordinal.
  mixNumber: number;
  numBaskets: number;
  multipleMixes: boolean;
  heats: number;
  customerMixId: string | null;
}

export type PlanTableMaterial =
  | { material: number; summary?: undefined }
  | { summary: "total" }
  | { summary: "price" };

export type ConsumptionKey =
  | "inventoryInitial"
  | "consumption"
  | "inventoryFinal"
  | "obtainableRequired"
  | "obtainableRemaining";

type MaterialQuantities = Record<number, number>;

const summarise = (materials: MaterialQuantities) => {
  return {
    total: Object.values(materials).reduce((left, right) => left + right, 0),
    maximum: Math.max(...Object.values(materials)),
  };
};

// Basket totals are basket-only; hopper / DRI materials live in the furnace.
export const combineMixMaterials = (
  mix: MixRead
): { material_name: string; mass: number }[] => {
  const massByMaterial = groupBy(
    [
      ...mix.basket_totals.map((total) => ({
        material_name: total.material_name,
        mass: total.mass,
      })),
      ...mix.furnace.map((material) => ({
        material_name: material.material_name,
        mass: material.quantity.value,
      })),
    ],
    (entry) => entry.material_name,
    (entries) => entries.reduce((sum, entry) => sum + entry.mass, 0)
  );
  return Object.entries(massByMaterial).map(([material_name, mass]) => ({
    material_name,
    mass,
  }));
};

const buildPlanMix = (
  result: ProductionMixResultRead,
  materialsByName: Record<string, MaterialRead>
): PlanMix => {
  const materials = groupBy(
    combineMixMaterials(result.mix),
    (entry) => materialsByName[entry.material_name]!.id,
    (entries) => entries[0]!.mass
  );
  const cost = result.mix.summary?.cost;
  return {
    materials,
    ...summarise(materials),
    total_price: cost?.display_cost_per_tonne ?? 0,
    materials_price: cost?.display_cost_per_tonne_materials ?? 0,
    electrical_energy_price:
      cost?.display_cost_per_tonne_electrical_energy ?? 0,
    charged_price: cost?.cost_per_tonne_charged ?? 0,
  };
};

export const withMaterials = (
  results: ProductionMixResultRead[]
): ProductionMixResultRead[] =>
  results.filter(
    (result) =>
      result.mix.basket_totals.length > 0 || result.mix.furnace.length > 0
  );

export const buildProductionMixes = (
  results: ProductionMixResultRead[],
  groupIdOf: (result: ProductionMixResultRead) => number,
  materialsByName: Record<string, MaterialRead>
): PlanMixes =>
  groupBy(withMaterials(results), groupIdOf, (groupResults) =>
    groupBy(
      groupResults,
      (result) => result.mix.id,
      (mixResults) => buildPlanMix(mixResults[0]!, materialsByName)
    )
  );

export const buildProductionGroups = (
  results: ProductionMixResultRead[],
  groupIdOf: (result: ProductionMixResultRead) => number,
  mixNumberByMixId: Record<number, number>,
  customerMixIdByMixId: Record<number, string | null>
): PlanGroup[] => {
  const resultsByGroup = groupBy(
    withMaterials(results),
    groupIdOf,
    (groupResults) => groupResults
  );
  return sorted(Object.entries(resultsByGroup), ([groupId]) =>
    parseInt(groupId)
  ).flatMap(([groupId, groupResults]) =>
    sorted(
      groupResults.map((result) => ({
        groupId: parseInt(groupId),
        mixId: result.mix.id,
        mixNumber: mixNumberByMixId[result.mix.id] ?? 1,
        numBaskets: result.mix.baskets.filter(
          (basket) => basket.layers.length > 0
        ).length,
        multipleMixes: groupResults.length > 1,
        heats: result.num_heats,
        customerMixId: customerMixIdByMixId[result.mix.id] ?? null,
      })),
      (group) => group.mixNumber
    )
  );
};

export const buildRecipeText = (
  result: ProductionMixResultRead,
  percentageBreakdown: boolean,
  formatValue: (value: number) => string,
  unitString: string
): string => {
  const materials = combineMixMaterials(result.mix).filter(
    (material) => material.mass > 0
  );
  const total = materials.reduce((sum, material) => sum + material.mass, 0);
  const header = `${result.name}\n${"-".repeat(result.name.length)}\n`;
  const lines = materials.map((material) => {
    const value = percentageBreakdown
      ? total > 0
        ? (material.mass / total) * 100
        : 0
      : material.mass;
    return `${material.material_name}: ${formatValue(value)}${unitString}\n`;
  });
  return header + lines.join("");
};
